import {DrawFrmApp} from "../drawfrm/DrawFrmApp";
import {FrmVar} from "../drawfrm/FrmVar";
import {ManagedScene2} from "./ManagedScene2";

// a scene that rebuilds its content when the frame grid changes. Subclasses implement
// build() to create their drawables + layout; the base subscribes to
// frameBlocksChanged (structural changes: cells/sticks added / removed / converted),
// coalesces rebuilds onto the next frame, and tears the subscription down. Views whose
// ref-point layout doesn't fully re-propagate on a value edit can opt into rebuilding
// on any user FrmVar edit too via rebuildOnValueEdit(). The view transform (pan/zoom)
// is preserved across a rebuild.
export abstract class RebuildingScene extends ManagedScene2 {
    private structureSub: (() => void) | null = null;
    private valueSub: {cancel(): void} | null = null;
    private rebuildScheduled = false;
    // true while build()/resetContent run, so a FrmVar set emitted during a rebuild
    // can't schedule another rebuild (guards against a feedback loop).
    private building = false;

    // create the scene's drawables and call setupLayout. Runs on setup and again on
    // every rebuild, so it must not assume a clean-slate-only invariant beyond what
    // resetContent guarantees.
    protected abstract build(): void;

    // also rebuild on any user FrmVar edit (dimension commit / drag), not just
    // structural changes. Off by default; the top-down / frame-profile sections turn
    // it on because their profile geometry is positioned by ref-point links that don't
    // re-solve in place when a dimension changes.
    protected rebuildOnValueEdit(): boolean {
        return false;
    }

    setup(app: DrawFrmApp): void {
        super.setup(app);
        this.building = true;
        this.build();
        this.building = false;

        this.structureSub = this.elev.frameSpec.frameBlocksChanged.subscribe(() => this.scheduleRebuild());
        if(this.rebuildOnValueEdit()) {
            this.valueSub = FrmVar.anyUserSet.subscribe(() => this.scheduleRebuild());
        }
    }

    private scheduleRebuild() {
        if(this.building) return; // edit emitted mid-rebuild — ignore, we're already rebuilding
        if(this.rebuildScheduled) return;
        this.rebuildScheduled = true;
        // defer to the next frame so we rebuild after the current emit finishes (other
        // listeners, e.g. the grid view's own reconcile, run first), and coalesce a
        // burst of edits into one rebuild.
        requestAnimationFrame(() => {
            this.rebuildScheduled = false;
            if(!this.app) return; // scene was disposed before the rebuild ran
            this.building = true;
            this.resetContent();
            this.build();
            this.building = false;
        });
    }

    dispose(): void {
        this.structureSub?.();
        this.structureSub = null;
        this.valueSub?.cancel();
        this.valueSub = null;
        super.dispose();
    }
}
